import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Bell,
  BellOff,
  Calendar,
  Cake,
  CheckCircle2,
  Clock,
  X,
  XCircle,
} from 'lucide-react';
import { toast as sonnerToast } from 'sonner';
import { cn } from '@/lib/utils';
import { DEFAULT_LOCALE, formatDateValue } from '@/lib/locale';
import {
  requestPushPermission,
  useNotifications,
  type AppNotification,
  type NotificationType,
} from '@/hooks/useNotifications';

type NotificationFilter = 'all' | 'unread';

type NotificationMeta = {
  label: string;
  Icon: typeof Calendar;
  iconClassName: string;
  iconBoxClassName: string;
  pillClassName: string;
  unreadCardClassName: string;
  unreadDotClassName: string;
  toastBarClassName: string;
  toastGlowClassName: string;
};

const TYPE_META: Record<NotificationType, NotificationMeta> = {
  new_booking: {
    label: 'Reserva online',
    Icon: Calendar,
    iconClassName: 'text-[#165568]',
    iconBoxClassName: 'border-[#bfe7ee] bg-[#eefbfd]',
    pillClassName: 'bg-[#e9f8fb] text-[#165568]',
    unreadCardClassName: 'border-[#cceef3] bg-[#f7fdff]',
    unreadDotClassName: 'bg-[#2db1c3]',
    toastBarClassName: 'bg-gradient-to-r from-[#2db1c3] via-[#7ad8e1] to-[#165568]',
    toastGlowClassName: 'bg-[#a6eaf2]',
  },
  confirmed: {
    label: 'Confirmada',
    Icon: CheckCircle2,
    iconClassName: 'text-emerald-600',
    iconBoxClassName: 'border-emerald-200 bg-emerald-50',
    pillClassName: 'bg-emerald-50 text-emerald-700',
    unreadCardClassName: 'border-emerald-100 bg-emerald-50/70',
    unreadDotClassName: 'bg-emerald-500',
    toastBarClassName: 'bg-gradient-to-r from-emerald-400 via-emerald-300 to-teal-400',
    toastGlowClassName: 'bg-emerald-200',
  },
  cancelled: {
    label: 'Cancelada',
    Icon: XCircle,
    iconClassName: 'text-rose-600',
    iconBoxClassName: 'border-rose-200 bg-rose-50',
    pillClassName: 'bg-rose-50 text-rose-700',
    unreadCardClassName: 'border-rose-100 bg-rose-50/80',
    unreadDotClassName: 'bg-rose-500',
    toastBarClassName: 'bg-gradient-to-r from-[#ef7b7b] via-[#f39a87] to-[#f6c177]',
    toastGlowClassName: 'bg-rose-200',
  },
  birthday: {
    label: 'Cumpleanos',
    Icon: Cake,
    iconClassName: 'text-amber-600',
    iconBoxClassName: 'border-amber-200 bg-amber-50',
    pillClassName: 'bg-amber-50 text-amber-700',
    unreadCardClassName: 'border-amber-100 bg-amber-50/80',
    unreadDotClassName: 'bg-amber-500',
    toastBarClassName: 'bg-gradient-to-r from-[#f4b860] via-[#f7cf7a] to-[#f1a4c1]',
    toastGlowClassName: 'bg-amber-200',
  },
  new_waitlist: {
    label: 'Lista de espera',
    Icon: Clock,
    iconClassName: 'text-orange-600',
    iconBoxClassName: 'border-orange-200 bg-orange-50',
    pillClassName: 'bg-orange-50 text-orange-700',
    unreadCardClassName: 'border-orange-100 bg-orange-50/80',
    unreadDotClassName: 'bg-orange-500',
    toastBarClassName: 'bg-gradient-to-r from-orange-400 via-amber-300 to-orange-500',
    toastGlowClassName: 'bg-orange-200',
  },
};

function TypeIcon({ type, className }: { type: NotificationType; className?: string }) {
  const meta = TYPE_META[type];
  const Icon = meta.Icon;
  return <Icon className={cn('h-4 w-4', meta.iconClassName, className)} />;
}

function relativeTime(iso: string) {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return 'Ahora';
  if (diff < 3600) return `Hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Hace ${Math.floor(diff / 3600)} h`;
  return formatDateValue(iso, DEFAULT_LOCALE, { day: '2-digit', month: 'short' });
}

function NotifToast({
  notif,
  onDismiss,
  onOpenPanel,
}: {
  notif: AppNotification;
  onDismiss: () => void;
  onOpenPanel: () => void;
}) {
  const meta = TYPE_META[notif.type];

  return (
    <div
      className="fixed bottom-5 right-5 z-[300] w-[360px] max-w-[calc(100dvw-1.5rem)] overflow-hidden rounded-3xl border border-white/70 bg-white/95 shadow-[0_28px_60px_-28px_rgba(13,62,78,0.52)] backdrop-blur animate-slide-in-up"
      style={{ fontFamily: "'Montserrat', sans-serif" }}
    >
      <div className={cn('h-1.5 w-full', meta.toastBarClassName)} />
      <div className="relative px-4 py-4">
        <div className={cn('pointer-events-none absolute inset-x-8 top-2 h-20 rounded-full blur-3xl opacity-40', meta.toastGlowClassName)} />
        <div className="relative flex items-start gap-3">
          <div className={cn('mt-0.5 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl border shadow-sm', meta.iconBoxClassName)}>
            <TypeIcon type={notif.type} className="h-5 w-5" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className={cn('rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em]', meta.pillClassName)}>
                {meta.label}
              </span>
              <span className="text-[10px] font-medium text-slate-400">
                {relativeTime(notif.created_at)}
              </span>
            </div>

            <p className="mt-2 text-sm font-semibold leading-snug text-slate-800">
              {notif.title}
            </p>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              {notif.body}
            </p>

            <button
              onClick={onOpenPanel}
              className="mt-3 inline-flex items-center rounded-full border border-[#bfe7ee] bg-[#eefbfd] px-3 py-1.5 text-[11px] font-semibold text-[#165568] transition-colors hover:bg-[#e3f8fb]"
            >
              Ver historial
            </button>
          </div>

          <button
            onClick={onDismiss}
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
            aria-label="Cerrar notificacion"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function NotifRow({
  notif,
  onMarkRead,
}: {
  notif: AppNotification;
  onMarkRead: (id: string) => void;
}) {
  const meta = TYPE_META[notif.type];

  return (
    <div
      className={cn(
        'rounded-2xl border px-3.5 py-3.5 shadow-[0_18px_34px_-30px_rgba(22,85,104,0.35)] transition-all',
        notif.read
          ? 'border-slate-200/80 bg-white'
          : meta.unreadCardClassName,
      )}
    >
      <div className="flex items-start gap-3">
        <div className={cn('mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl border', meta.iconBoxClassName)}>
          <TypeIcon type={notif.type} className="h-4.5 w-4.5" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={cn('rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em]', meta.pillClassName)}>
              {meta.label}
            </span>
            {!notif.read && (
              <span className={cn('h-2.5 w-2.5 rounded-full shadow-[0_0_0_4px_rgba(255,255,255,0.9)]', meta.unreadDotClassName)} />
            )}
            <span className="text-[10px] font-medium text-slate-400">
              {relativeTime(notif.created_at)}
            </span>
          </div>

          <p className="mt-2 text-sm font-semibold leading-snug text-slate-800">
            {notif.title}
          </p>
          <p className="mt-1 text-xs leading-5 text-slate-500">
            {notif.body}
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {!notif.read ? (
              <button
                onClick={() => onMarkRead(notif.id)}
                className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-600 transition-colors hover:border-[#bfe7ee] hover:bg-[#eefbfd] hover:text-[#165568]"
              >
                Marcar leida
              </button>
            ) : (
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                Leida
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function NotificationBell() {
  const {
    notifications,
    unreadCount,
    toast,
    dismissToast,
    markAsRead,
    markAllRead,
  } = useNotifications();

  const [open, setOpen] = useState(false);
  const [ringing, setRinging] = useState(false);
  const [pushState, setPushState] = useState<'default' | 'granted' | 'denied'>('default');
  const [filter, setFilter] = useState<NotificationFilter>('all');

  const panelRef = useRef<HTMLDivElement>(null);
  const prevCount = useRef(0);
  const didInitCount = useRef(false);

  useEffect(() => {
    if ('Notification' in window) setPushState(Notification.permission);
  }, []);

  const handleEnablePush = async () => {
    const granted = await requestPushPermission();
    setPushState(granted ? 'granted' : 'denied');
  };

  useEffect(() => {
    if (!didInitCount.current) {
      didInitCount.current = true;
      prevCount.current = unreadCount;
      return;
    }

    if (unreadCount > prevCount.current) {
      prevCount.current = unreadCount;
      setRinging(true);
      const timeoutId = setTimeout(() => setRinging(false), 900);
      return () => clearTimeout(timeoutId);
    }

    prevCount.current = unreadCount;
  }, [unreadCount]);

  useEffect(() => {
    if (!open) return;

    const handleOutsideClick = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  const visibleNotifications = useMemo(() => (
    filter === 'unread'
      ? notifications.filter((notification) => !notification.read)
      : notifications
  ), [filter, notifications]);

  const handleMarkRead = async (notificationId: string) => {
    const success = await markAsRead(notificationId);
    if (!success) {
      sonnerToast.error('No se pudo marcar la notificacion como leida');
    }
  };

  const handleMarkAllRead = async () => {
    const success = await markAllRead();
    if (!success) {
      sonnerToast.error('No se pudieron actualizar las notificaciones');
    }
  };

  const handleOpenFromToast = () => {
    setOpen(true);
    dismissToast();
  };

  return (
    <>
      <div ref={panelRef} className="relative">
        <button
          onClick={() => setOpen((current) => !current)}
          className="relative flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-white/10"
          aria-label="Notificaciones"
        >
          {unreadCount > 0 && (
            <svg
              className="pointer-events-none absolute inset-0 h-full w-full animate-spin-slow"
              viewBox="0 0 36 36"
              fill="none"
            >
              <circle
                cx="18"
                cy="18"
                r="15"
                stroke="#2db1c3"
                strokeWidth="2"
                strokeDasharray="5 3.5"
                strokeLinecap="round"
              />
            </svg>
          )}

          <Bell
            className={cn(
              'h-5 w-5 text-white/75',
              ringing && 'animate-bell',
            )}
          />

          {unreadCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold leading-none text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        {open && (
          <div
            // En mobile no se puede confiar en `right-0` respecto al propio
            // boton: no es el ultimo icono del header (el avatar queda
            // despues), asi que un panel ancho anclado a su borde se corre
            // hacia la izquierda y queda pegado/montado sobre el resto de la
            // pantalla. `fixed` con margenes de viewport lo deja contenido
            // sin importar donde este el boton que lo abre. En desktop (sm+)
            // se mantiene el dropdown anclado de siempre.
            className="fixed inset-x-3 top-14 z-[200] overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_28px_70px_-28px_rgba(13,62,78,0.45)] sm:absolute sm:inset-x-auto sm:right-0 sm:top-11 sm:w-[24rem] sm:max-w-[calc(100dvw-1.5rem)]"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            <div className="border-b border-slate-200/80 bg-[linear-gradient(180deg,#f7fcfd_0%,#ffffff_100%)] px-4 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-[#165568]">
                      Notificaciones
                    </span>
                    <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#e9f8fb] px-1.5 text-[10px] font-bold text-[#165568]">
                      {unreadCount}
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] text-slate-500">
                    Historial reciente de tu salon en tiempo real
                  </p>
                </div>

                <button
                  onClick={() => setOpen(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                  aria-label="Cerrar panel de notificaciones"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={() => setFilter('all')}
                  className={cn(
                    'rounded-full px-3 py-1.5 text-[11px] font-semibold transition-colors',
                    filter === 'all'
                      ? 'bg-[#165568] text-white shadow-sm'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200/80',
                  )}
                >
                  Todas ({notifications.length})
                </button>
                <button
                  onClick={() => setFilter('unread')}
                  className={cn(
                    'rounded-full px-3 py-1.5 text-[11px] font-semibold transition-colors',
                    filter === 'unread'
                      ? 'bg-[#2db1c3] text-white shadow-sm'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200/80',
                  )}
                >
                  Sin leer ({unreadCount})
                </button>

                <button
                  onClick={() => void handleMarkAllRead()}
                  disabled={unreadCount === 0}
                  className="ml-auto rounded-full border border-[#bfe7ee] bg-[#eefbfd] px-3 py-1.5 text-[11px] font-semibold text-[#165568] transition-colors hover:bg-[#e3f8fb] disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                >
                  Marcar todas
                </button>
              </div>
            </div>

            {pushState === 'default' && (
              <button
                onClick={handleEnablePush}
                className="flex w-full items-center gap-2 border-b border-slate-200 bg-[#f2fbfc] px-4 py-3 text-left text-xs font-medium text-[#165568] transition-colors hover:bg-[#e7f8fb]"
              >
                <Bell className="h-3.5 w-3.5 flex-shrink-0" />
                Activar notificaciones del navegador
              </button>
            )}

            {pushState === 'denied' && (
              <div className="flex items-center gap-2 border-b border-slate-200 bg-rose-50 px-4 py-3 text-xs font-medium text-rose-600">
                <BellOff className="h-3.5 w-3.5 flex-shrink-0" />
                Notificaciones bloqueadas en el navegador
              </div>
            )}

            <div className="max-h-[420px] overflow-x-hidden overflow-y-auto overscroll-contain bg-slate-50/60 px-3 py-3">
              {visibleNotifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-10 text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100">
                    <Bell className="h-6 w-6 text-slate-300" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-700">
                      {filter === 'unread' ? 'No tienes notificaciones sin leer' : 'Aun no hay notificaciones'}
                    </p>
                    <p className="mt-1 text-xs leading-5 text-slate-400">
                      Cuando entren nuevas reservas, confirmaciones, cancelaciones o cumpleanos, apareceran aqui.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {visibleNotifications.map((notification) => (
                    <NotifRow
                      key={notification.id}
                      notif={notification}
                      onMarkRead={handleMarkRead}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {toast && (
        <NotifToast
          notif={toast}
          onDismiss={dismissToast}
          onOpenPanel={handleOpenFromToast}
        />
      )}
    </>
  );
}
